import { Input, message, theme } from 'antd'
import React, { useEffect, useRef } from 'react'
import { P, match } from 'ts-pattern'
import { Trans, useLingui } from '@lingui/react/macro'

import { ImageDisplayStatus } from '../ImageDisplay/type'

import type { TextAreaProps } from 'antd/es/input'


import { cls } from 'tagged-classnames-free';
import { CssIcon } from '@/components/CssIcon';
export interface EnhanceTextAreaProps
  extends Omit<TextAreaProps, 'variant' | 'value'> {
  value?: string
  extra?: React.ReactNode
  containerClassName?: string
  containerStyle?: React.CSSProperties
  imageDisplayStatus?: ImageDisplayStatus
  imageUrl?: string
  showImage?: boolean
  showClear?: boolean
  showCopy?: boolean
  onImageRemove?: () => void
  autoScrollToBottom?: boolean
  disabledCopy?: boolean
  disabledClear?: boolean
  bottomChildren?: React.ReactNode
  topChildren?: React.ReactNode
  innerLeftChildren?: React.ReactNode
  disabled?: boolean
  showMainContent?: boolean
  resizeIconClassName?: string
}

const EnhanceTextArea: React.FC<EnhanceTextAreaProps> = (props) => {
  const {
    containerClassName,
    containerStyle,
    extra,
    value = '',
    className,
    count,
    onChange,
    // omit
    showCount,
    maxLength,
    imageDisplayStatus = ImageDisplayStatus.EMPTY,
    imageUrl = '',
    showImage = false,
    showClear = false,
    showCopy = false,
    onImageRemove,
    autoScrollToBottom = false,
    disabledCopy = false,
    disabledClear = false,
    bottomChildren = null, // 自定义底部内容
    disabled = false,
    topChildren = null, // 自定义顶部内容
    innerLeftChildren = null, // 输入框内部左侧内容
    showMainContent = true,
    resizeIconClassName,
    ...restTextAreaProps
  } = props
  const { t } = useLingui()
  const { hashId } = theme.useToken()
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const prevValueLengthRef = useRef(value.length)
  const isUserInputRef = useRef(false)

  const countMax = count?.max || 200

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value
    const currentValue = value || ''

    // 标记这是用户输入
    isUserInputRef.current = true

    // 超出字数限制时截断到最大长度，删除操作始终允许
    if (count?.max) {
      const currentLength = count?.strategy?.(newValue) || newValue.length
      const prevLength = count?.strategy?.(currentValue) || currentValue.length

      if (currentLength > count.max && currentLength > prevLength) {
        // 截断超出部分，保留不超出的内容
        let truncated = newValue
        if (count?.strategy) {
          // 自定义策略（如 getInputWidth），先粗切到 max 字符数（每字符宽度 >= 1），再微调
          const chars = Array.from(truncated).slice(0, count.max)
          while (
            chars.length > 0 &&
            count.strategy(chars.join('')) > count.max
          ) {
            chars.pop()
          }
          truncated = chars.join('')
        } else {
          truncated = newValue.slice(0, count.max)
        }

        // 截断后与当前值相同，无需更新
        if (truncated === currentValue) return

        const truncatedEvent = {
          ...e,
          target: { ...e.target, value: truncated },
        } as React.ChangeEvent<HTMLTextAreaElement>
        onChange?.(truncatedEvent)
        return
      }
    }

    onChange?.(e)
  }

  // 默认的底部内容
  const defaultBottomChildren = (
    <div className='flex justify-between gap-2'>
      <div className='scrollbar flex flex-1 items-center overflow-x-auto pl-[11px]'>
        {extra}
        {match(count?.show)
          .with(
            P.when((show) => {
              return !!show
            }),
            () => {
              const currentLength = count?.strategy
                ? count.strategy(value)
                : value.length

              return (
                <div className={`text-f-text-quaternary py-2 text-xs`}>
                  {currentLength}/{countMax} <Trans>characters</Trans>
                </div>
              )
            },
          )
          .otherwise(() => {
            return null
          })}
      </div>
      <div className='flex items-center gap-3 pb-[2px] pt-2'>
        {showCopy && (
          <div
            className={cls`text-f-text-quaternary cursor-pointer transition-colors ${disabledCopy ? 'cursor-not-allowed opacity-40' : 'hover:text-f-text-secondary'}`}
            onClick={async () => {
              if (disabledCopy) return
              try {
                await navigator.clipboard.writeText(value)
                message.success(t`Copied successfully!`)
              } catch (err) {
                message.error(t`Copy failed!`)
              }
            }}
          >
            <span className='i-com--copy size-4'></span>
          </div>
        )}
        {showClear && (
          <div
            className={cls`text-f-text-quaternary cursor-pointer transition-colors ${disabledClear ? 'cursor-not-allowed opacity-40' : 'hover:text-f-text-secondary'}`}
            onClick={() => {
              if (disabledClear) return
              // 创建一个空的 change event 来清空内容
              const fakeEvent = {
                target: { value: '' },
              } as React.ChangeEvent<HTMLTextAreaElement>
              onChange?.(fakeEvent)
              message.success(t`Cleared successfully!`)
            }}
          >
            <span className='i-com--trash size-4'></span>
          </div>
        )}
      </div>
    </div>
  )

  // 自动滚动跟随最新内容的逻辑
  useEffect(() => {
    if (!autoScrollToBottom || !textareaRef.current || !value) {
      return
    }

    const currentValueLength = value.length || 0
    const prevValueLength = prevValueLengthRef.current || 0

    // 只有在内容增加且不是用户输入时才自动滚动到最新内容
    if (currentValueLength > prevValueLength && !isUserInputRef.current) {
      const textarea = textareaRef.current

      // 确保滚动到最新内容
      const scrollToBottom = () => {
        try {
          // 尝试通过 DOM 查询找到真正的 textarea 元素
          const antdTextarea = textarea as any
          const realTextarea =
            antdTextarea.resizableTextArea?.textArea ||
            textarea.querySelector?.('textarea') ||
            textarea

          // 强制滚动到底部
          if (realTextarea && typeof realTextarea.scrollTop !== 'undefined') {
            realTextarea.scrollTop = realTextarea.scrollHeight
          }
        } catch (error) {
          // 如果出错，使用最基础的滚动方式
          if (textarea && typeof textarea.scrollTop !== 'undefined') {
            textarea.scrollTop = textarea.scrollHeight || 0
          }
        }
      }

      // 立即执行一次
      scrollToBottom()

      // 延迟执行确保DOM完全更新
      setTimeout(scrollToBottom, 0)
      setTimeout(scrollToBottom, 50)
    }

    // 重置用户输入标记
    isUserInputRef.current = false
    // 更新上次的值长度
    prevValueLengthRef.current = currentValueLength
  }, [value, autoScrollToBottom])

  return (
    <div
      className={cls`
        ${hashId} focus-within:bg-f-bg-elevated coco-input coco-input-outlined 
        inline-block pl-0 ${containerClassName}
        after:bg-f-bg-elevated
      `}
      style={containerStyle}
    >
      {topChildren || null}
      <div className={`relative ${showMainContent ? '' : 'hidden'}`}>
        <div className='flex items-start pr-0.5'>
          {innerLeftChildren && (
            <div className='shrink-0'>{innerLeftChildren}</div>
          )}
          <div className='relative min-w-0 flex-1'>
            <Input.TextArea
              disabled={disabled}
              ref={textareaRef}
              rows={6}
              value={value}
              className={cls`scrollbar placeholder:text-base placeholder:font-normal ${className}`}
              count={{
                strategy: count?.strategy,
                max: countMax,
              }}
              {...restTextAreaProps}
              onChange={handleChange}
              variant='borderless'
              suppressHydrationWarning
            />
            {restTextAreaProps.style?.resize === 'none' ? null : (
              <CssIcon
                className={cls`i-cus--tad-subscript size-3 ${resizeIconClassName || ''}`}
                containerClassName={cls`bg-f-bg-elevated pointer-events-none absolute -bottom-px right-0`}
              ></CssIcon>
            )}
          </div>
        </div>
      </div>
      <div className={`${showMainContent ? '' : 'hidden'}`}>
        {bottomChildren || defaultBottomChildren}
      </div>
    </div>
  )
}

export default EnhanceTextArea
